import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";

export default function DatenschutzPage() {
  return (
    <div data-testid="datenschutz-page" className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>Datenschutzerklärung | Oona Flats</title>
        <meta name="description" content="Datenschutzerklärung der SharedSpace GmbH gemäß DSGVO" />
      </Helmet>
      <Navbar />

      <div className="pt-28 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <h1
              className="text-4xl md:text-5xl font-semibold tracking-tighter text-[#1D1D1F] mb-4"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Datenschutzerklärung
            </h1>
            <p className="text-sm text-[#86868B] mb-12">Stand: Mai 2026</p>
          </Reveal>

          <Reveal delay={150}>
          <div className="bg-white rounded-3xl border border-black/5 p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.04)] space-y-10">

            {/* 1. Verantwortlicher */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                1. Verantwortlicher
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-1">
                <p className="font-medium">SharedSpace GmbH</p>
                <p>Cortigasse 19/5, 1220 Wien, Österreich</p>
                <p>E-Mail: <a href="mailto:office@sharedspace-rentals.com" className="underline hover:text-[#86868B]">office@sharedspace-rentals.com</a></p>
                <p>Geschäftsführer: Lukas Krämer, Stephan Szentgyörgyi</p>
                <p>UID-Nr.: ATU80794712 | FN 626272 p, Handelsgericht Wien</p>
              </div>
            </section>

            {/* 2. Überblick */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                2. Überblick der Verarbeitungen
              </h2>
              <p className="text-sm text-[#1D1D1F] leading-relaxed">
                Die nachfolgende Übersicht fasst die Arten der verarbeiteten Daten und die Zwecke ihrer Verarbeitung zusammen und verweist auf die betroffenen Personen.
              </p>
              <ul className="mt-3 text-sm text-[#1D1D1F] leading-relaxed list-disc list-inside space-y-1">
                <li>Bestandsdaten (z.B. Namen, Adressen)</li>
                <li>Kontaktdaten (z.B. E-Mail, Telefonnummern)</li>
                <li>Inhaltsdaten (z.B. Eingaben in Formularen)</li>
                <li>Vertragsdaten (z.B. Buchungsinformationen, Zahlungsdaten)</li>
                <li>Nutzungsdaten (z.B. besuchte Seiten, Zugriffszeiten)</li>
                <li>Meta-/Kommunikationsdaten (z.B. Geräte-Informationen, IP-Adressen)</li>
              </ul>
            </section>

            {/* 3. Rechtsgrundlagen */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                3. Rechtsgrundlagen
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>Im Folgenden erhalten Sie eine Übersicht der Rechtsgrundlagen der DSGVO, auf deren Basis wir personenbezogene Daten verarbeiten:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Einwilligung (Art. 6 Abs. 1 lit. a DSGVO)</strong> – Die betroffene Person hat ihre Einwilligung zur Verarbeitung gegeben.</li>
                  <li><strong>Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO)</strong> – Die Verarbeitung ist zur Erfüllung eines Vertrags (z.B. Buchung) erforderlich.</li>
                  <li><strong>Rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO)</strong> – Die Verarbeitung ist zur Erfüllung einer rechtlichen Verpflichtung erforderlich.</li>
                  <li><strong>Berechtigte Interessen (Art. 6 Abs. 1 lit. f DSGVO)</strong> – Die Verarbeitung ist zur Wahrung berechtigter Interessen erforderlich.</li>
                </ul>
              </div>
            </section>

            {/* 4. Buchung & Zahlung */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                4. Buchung und Zahlungsabwicklung
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>
                  Bei einer Buchung über unsere Website verarbeiten wir folgende Daten zur Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO):
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Name, E-Mail-Adresse, Telefonnummer</li>
                  <li>Buchungszeitraum (An-/Abreisedatum), Gästeanzahl</li>
                  <li>Gewählte Unterkunft und Preisinformationen</li>
                </ul>
                <p>
                  Die Zahlungsabwicklung erfolgt über <strong>Stripe</strong> (Stripe Technology Europe Ltd., Dublin, Irland). Bei der Bezahlung werden Sie auf die Stripe-Checkout-Seite weitergeleitet. Wir erhalten keine vollständigen Kreditkarten- oder Kontodaten. Stripe verarbeitet Ihre Zahlungsdaten gemäß deren eigener Datenschutzerklärung:{" "}
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#86868B]">stripe.com/privacy</a>
                </p>
                <p>
                  Buchungsdaten werden an <strong>Hostaway</strong> (unser Channel-Management-System) zur Reservierungsverwaltung übermittelt.
                </p>
              </div>
            </section>

            {/* 5. Kontaktformular */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                5. Kontaktformular und E-Mail-Kommunikation
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>
                  Bei der Kontaktaufnahme über unser Kontaktformular werden folgende Daten verarbeitet:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Name, E-Mail-Adresse</li>
                  <li>Betreff und Nachrichteninhalt</li>
                </ul>
                <p>
                  Die Verarbeitung erfolgt auf Basis von Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen) bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung). Die Daten werden nach Erledigung der Anfrage und Ablauf gesetzlicher Aufbewahrungsfristen gelöscht.
                </p>
                <p>
                  Für den E-Mail-Versand nutzen wir <strong>Resend</strong> (Resend Inc., USA). Die Übermittlung erfolgt auf Basis von Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).
                </p>
              </div>
            </section>

            {/* 6. Hosting */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                6. Hosting und Infrastruktur
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>
                  Unsere Website wird auf Servern eines professionellen Hosting-Anbieters betrieben. Beim Zugriff auf unsere Website werden automatisch folgende Daten in Server-Logfiles gespeichert:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>IP-Adresse des anfragenden Geräts</li>
                  <li>Datum und Uhrzeit des Zugriffs</li>
                  <li>Aufgerufene URL / Seite</li>
                  <li>Verwendeter Browser und Betriebssystem</li>
                  <li>Referrer-URL (zuvor besuchte Seite)</li>
                </ul>
                <p>
                  Die Verarbeitung erfolgt auf Basis unseres berechtigten Interesses an der Bereitstellung und Sicherheit unserer Website (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
              </div>
            </section>

            {/* 7. Cookies */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                7. Cookies und lokale Speicherung
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>
                  Unsere Website verwendet ausschließlich <strong>technisch notwendige Speicherungen</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Spracheinstellung</strong> (localStorage): Speichert Ihre Sprachpräferenz (DE/EN) lokal in Ihrem Browser. Es werden keine Daten an unseren Server übermittelt.</li>
                </ul>
                <p>
                  Wir setzen keine Tracking-Cookies, keine Analyse-Tools (kein Google Analytics) und keine Werbe-Cookies ein.
                </p>
              </div>
            </section>

            {/* 8. Kartendienste */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                8. Kartendienste
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>
                  Für die Darstellung von Standortkarten verwenden wir <strong>OpenStreetMap</strong> über Carto-Tiles. Beim Laden der Kartenkacheln wird Ihre IP-Adresse an die Server von OpenStreetMap/Carto übermittelt. Die Nutzung erfolgt auf Basis unseres berechtigten Interesses (Art. 6 Abs. 1 lit. f DSGVO) an der ansprechenden Darstellung unserer Unterkünfte.
                </p>
                <p>
                  Datenschutzerklärung von OpenStreetMap:{" "}
                  <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#86868B]">osmfoundation.org/Privacy_Policy</a>
                </p>
              </div>
            </section>

            {/* 9. Drittanbieter */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                9. Auftragsverarbeiter und Drittanbieter
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed">
                <div className="overflow-x-auto">
                  <table className="w-full mt-2 border-collapse">
                    <thead>
                      <tr className="border-b border-black/10">
                        <th className="text-left py-2 pr-4 font-semibold">Dienst</th>
                        <th className="text-left py-2 pr-4 font-semibold">Anbieter</th>
                        <th className="text-left py-2 font-semibold">Zweck</th>
                      </tr>
                    </thead>
                    <tbody className="text-[#86868B]">
                      <tr className="border-b border-black/5">
                        <td className="py-2 pr-4">Stripe</td>
                        <td className="py-2 pr-4">Stripe Technology Europe Ltd., IE</td>
                        <td className="py-2">Zahlungsabwicklung</td>
                      </tr>
                      <tr className="border-b border-black/5">
                        <td className="py-2 pr-4">Hostaway</td>
                        <td className="py-2 pr-4">Hostaway Inc., USA</td>
                        <td className="py-2">Channel-Management, Reservierungen</td>
                      </tr>
                      <tr className="border-b border-black/5">
                        <td className="py-2 pr-4">Resend</td>
                        <td className="py-2 pr-4">Resend Inc., USA</td>
                        <td className="py-2">E-Mail-Versand</td>
                      </tr>
                      <tr className="border-b border-black/5">
                        <td className="py-2 pr-4">OpenStreetMap / Carto</td>
                        <td className="py-2 pr-4">OSM Foundation, UK</td>
                        <td className="py-2">Kartendarstellung</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Google Fonts</td>
                        <td className="py-2 pr-4">Google LLC, USA</td>
                        <td className="py-2">Schriftarten</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-3">
                  Mit Anbietern in Drittländern (USA) bestehen Standardvertragsklauseln gemäß Art. 46 Abs. 2 lit. c DSGVO als Grundlage für die Datenübermittlung.
                </p>
              </div>
            </section>

            {/* 10. Speicherdauer */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                10. Speicherdauer
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-2">
                <p>Wir speichern personenbezogene Daten nur so lange, wie es für die jeweiligen Zwecke erforderlich ist:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Buchungsdaten:</strong> 7 Jahre (gesetzliche Aufbewahrungspflicht gem. BAO/UGB)</li>
                  <li><strong>Kontaktanfragen:</strong> 6 Monate nach Erledigung</li>
                  <li><strong>Server-Logfiles:</strong> 14 Tage</li>
                </ul>
              </div>
            </section>

            {/* 11. Betroffenenrechte */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                11. Ihre Rechte als betroffene Person
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-3">
                <p>Ihnen stehen gemäß DSGVO folgende Rechte zu:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Auskunftsrecht</strong> (Art. 15 DSGVO) – Sie können Auskunft über Ihre bei uns gespeicherten Daten verlangen.</li>
                  <li><strong>Recht auf Berichtigung</strong> (Art. 16 DSGVO) – Sie können die Berichtigung unrichtiger Daten verlangen.</li>
                  <li><strong>Recht auf Löschung</strong> (Art. 17 DSGVO) – Sie können die Löschung Ihrer Daten verlangen, sofern keine gesetzliche Aufbewahrungspflicht besteht.</li>
                  <li><strong>Recht auf Einschränkung</strong> (Art. 18 DSGVO) – Sie können die Einschränkung der Verarbeitung verlangen.</li>
                  <li><strong>Recht auf Datenübertragbarkeit</strong> (Art. 20 DSGVO) – Sie können Ihre Daten in einem strukturierten Format erhalten.</li>
                  <li><strong>Widerspruchsrecht</strong> (Art. 21 DSGVO) – Sie können der Verarbeitung auf Basis berechtigter Interessen widersprechen.</li>
                  <li><strong>Recht auf Widerruf der Einwilligung</strong> (Art. 7 Abs. 3 DSGVO) – Erteilte Einwilligungen können jederzeit widerrufen werden.</li>
                </ul>
                <p>
                  Zur Ausübung Ihrer Rechte wenden Sie sich bitte an:{" "}
                  <a href="mailto:office@sharedspace-rentals.com" className="underline hover:text-[#86868B]">office@sharedspace-rentals.com</a>
                </p>
              </div>
            </section>

            {/* 12. Beschwerderecht */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                12. Beschwerderecht bei der Aufsichtsbehörde
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed space-y-2">
                <p>
                  Sie haben das Recht, sich bei der zuständigen Datenschutzbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen die DSGVO verstößt:
                </p>
                <div className="bg-[#F5F5F7] rounded-xl p-4 mt-2">
                  <p className="font-medium">Österreichische Datenschutzbehörde</p>
                  <p>Barichgasse 40-42, 1030 Wien</p>
                  <p>Telefon: +43 1 52 152-0</p>
                  <p>E-Mail: <a href="mailto:dsb@dsb.gv.at" className="underline hover:text-[#86868B]">dsb@dsb.gv.at</a></p>
                  <p>Web: <a href="https://www.dsb.gv.at" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#86868B]">www.dsb.gv.at</a></p>
                </div>
              </div>
            </section>

            {/* 13. Sicherheit */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                13. Datensicherheit
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed">
                <p>
                  Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre Daten gegen Manipulation, Verlust, Zerstörung oder unbefugten Zugriff zu schützen. Unsere Website ist SSL/TLS-verschlüsselt (HTTPS). Unsere Sicherheitsmaßnahmen werden entsprechend der technologischen Entwicklung fortlaufend verbessert.
                </p>
              </div>
            </section>

            {/* 14. Änderungen */}
            <section>
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                14. Änderungen dieser Datenschutzerklärung
              </h2>
              <div className="text-sm text-[#1D1D1F] leading-relaxed">
                <p>
                  Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen umzusetzen. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
                </p>
              </div>
            </section>

          </div>
          </Reveal>
        </div>
      </div>

      <Footer />
    </div>
  );
}
